// IndexedDB store for user-pinned entity IDs.
//
// Generic shape: one durable list of (id, pinned_at) tuples, namespaced per
// tenant + entity kind. The hook (usePinned) is the only consumer; product
// code never touches IDB directly.
//
// Database layout:
//   DB:     "eidollon-pins" (version 1, never bumped after initial create)
//   Store:  "pins" — key = composite "{tenantId}|{entityKey}|{id}",
//                    value = { tenant, entity, id, pinned_at }
//   Index:  "by_scope" on [tenant, entity] for efficient per-tenant list.
//
// Namespacing as a composite key (instead of one IDB store per tenant)
// avoids `IDBDatabase.version` bumps when a new tenant or new entity kind
// shows up at runtime — a version bump would block other open tabs and is
// the wrong tool for "user just opened a new browser/tenant pair".
//
// Tenant isolation:
//   * Every read uses the `by_scope` index keyed by [tenant, entity], so
//     tenant A's list query physically cannot see tenant B's pins.
//   * Every write embeds tenant + entity in the row; deletes use the same
//     composite primary key.
//
// Versioning:
//   * The IDB schema version is 1 and stays 1.
//   * The store name itself embeds `:v1` semantics via the `:v1:` prefix on
//     the COMPOSITE KEY. A future shape change ships under a new prefix and
//     the old keys are read-only/ignored.

export const PINS_DB_NAME = 'eidollon-pins';
export const PINS_DB_VERSION = 1;
export const PINS_STORE = 'pins';
export const PINS_INDEX_BY_SCOPE = 'by_scope';

export interface PinnedEntry {
  id: string;
  /** ms-epoch when the user pinned this id. Newest first when sorted desc. */
  pinned_at: number;
}

interface StoredPinRow {
  /** Composite primary key. */
  key: string;
  tenant: string;
  entity: string;
  id: string;
  pinned_at: number;
}

export interface PinnedStore {
  add(id: string): Promise<PinnedEntry>;
  remove(id: string): Promise<void>;
  has(id: string): Promise<boolean>;
  /** All entries for this (tenant, entity), newest-pin first. */
  list(): Promise<PinnedEntry[]>;
  clear(): Promise<void>;
  close(): void;
  /** Logical store name used by the broadcast channel (tenant + entity). */
  readonly storeName: string;
}

export interface OpenPinnedStoreOptions {
  /** Tenant the pinned list is scoped to. */
  tenantId: string;
  /** Entity kind, e.g. "opp", "intel", "vendor". Keep it lowercase + short. */
  entityKey: string;
  /** Override for tests (fake-indexeddb). */
  factory?: IDBFactory;
}

/**
 * Build a deterministic, broadcast-safe scope name for a (tenant, entity).
 * Used as the `BroadcastChannel` name and the `localStorage` rev-key prefix.
 * Sanitised to ascii-safe characters for grep/DevTools friendliness.
 */
export function pinsStoreName(tenantId: string, entityKey: string): string {
  const safe = (s: string) => s.toLowerCase().replace(/[^a-z0-9-]/g, '_');
  return `pins:v1:${safe(tenantId)}:${safe(entityKey)}`;
}

function compositeKey(tenant: string, entity: string, id: string): string {
  // The `|` separator is illegal inside our sanitised scope names so
  // collisions are not possible.
  return `${tenant}|${entity}|${id}`;
}

export async function openPinnedStore(opts: OpenPinnedStoreOptions): Promise<PinnedStore> {
  const factory = opts.factory ?? (typeof indexedDB !== 'undefined' ? indexedDB : null);
  if (!factory) throw new Error('IndexedDB not available in this environment');

  const tenant = opts.tenantId;
  const entity = opts.entityKey;

  const db = await new Promise<IDBDatabase>((resolve, reject) => {
    const req = factory.open(PINS_DB_NAME, PINS_DB_VERSION);
    req.onupgradeneeded = () => {
      const upDb = req.result;
      if (!upDb.objectStoreNames.contains(PINS_STORE)) {
        const os = upDb.createObjectStore(PINS_STORE, { keyPath: 'key' });
        os.createIndex(PINS_INDEX_BY_SCOPE, ['tenant', 'entity'], { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('idb open failed'));
  });

  function tx(mode: IDBTransactionMode) {
    return db.transaction(PINS_STORE, mode).objectStore(PINS_STORE);
  }

  async function add(id: string): Promise<PinnedEntry> {
    const row: StoredPinRow = {
      key: compositeKey(tenant, entity, id),
      tenant,
      entity,
      id,
      pinned_at: Date.now(),
    };
    await new Promise<void>((resolve, reject) => {
      const t = db.transaction(PINS_STORE, 'readwrite');
      t.objectStore(PINS_STORE).put(row);
      t.oncomplete = () => resolve();
      t.onerror = () => reject(t.error);
      t.onabort = () => reject(t.error);
    });
    return { id: row.id, pinned_at: row.pinned_at };
  }

  async function remove(id: string): Promise<void> {
    await new Promise<void>((resolve, reject) => {
      const t = db.transaction(PINS_STORE, 'readwrite');
      t.objectStore(PINS_STORE).delete(compositeKey(tenant, entity, id));
      t.oncomplete = () => resolve();
      t.onerror = () => reject(t.error);
      t.onabort = () => reject(t.error);
    });
  }

  async function has(id: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const req = tx('readonly').getKey(compositeKey(tenant, entity, id));
      req.onsuccess = () => resolve(req.result !== undefined);
      req.onerror = () => reject(req.error);
    });
  }

  async function list(): Promise<PinnedEntry[]> {
    return new Promise((resolve, reject) => {
      const out: PinnedEntry[] = [];
      const ix = tx('readonly').index(PINS_INDEX_BY_SCOPE);
      const range = IDBKeyRange.only([tenant, entity]);
      const req = ix.openCursor(range);
      req.onsuccess = () => {
        const cur = req.result;
        if (!cur) {
          out.sort((a, b) => b.pinned_at - a.pinned_at);
          resolve(out);
          return;
        }
        const v = cur.value as StoredPinRow;
        out.push({ id: v.id, pinned_at: v.pinned_at });
        cur.continue();
      };
      req.onerror = () => reject(req.error);
    });
  }

  async function clear(): Promise<void> {
    // Scoped clear — only this (tenant, entity)'s rows.
    await new Promise<void>((resolve, reject) => {
      const t = db.transaction(PINS_STORE, 'readwrite');
      const ix = t.objectStore(PINS_STORE).index(PINS_INDEX_BY_SCOPE);
      const range = IDBKeyRange.only([tenant, entity]);
      const req = ix.openCursor(range);
      req.onsuccess = () => {
        const cur = req.result;
        if (!cur) return;
        cur.delete();
        cur.continue();
      };
      t.oncomplete = () => resolve();
      t.onerror = () => reject(t.error);
      t.onabort = () => reject(t.error);
    });
  }

  return {
    add,
    remove,
    has,
    list,
    clear,
    close: () => db.close(),
    storeName: pinsStoreName(tenant, entity),
  };
}
